import { Module, GetterTree, ActionTree, MutationTree } from 'vuex';
import http from '@/http';

export const HCS_QUESTION_GROUP_TYPE_PHYSICAL =
  'HCS_QUESTION_GROUP_TYPE_PHYSICAL';

export const HCS_QUESTION_GROUP_TYPE_PSYCHOLOGICAL =
  'HCS_QUESTION_GROUP_TYPE_PSYCHOLOGICAL';

export const HCS_TYPES = [
  {
    value: 'HCS_QUESTION_GROUP_TYPE_PHYSICAL',
    label: 'Physical',
  },
  {
    value: 'HCS_QUESTION_GROUP_TYPE_PSYCHOLOGICAL',
    label: 'Psychological',
  },
];

export const HCS_RECOMMENDATION_TYPE_STANDARD =
  'HCS_RECOMMENDATION_TYPE_STANDARD';

export const HCS_RECOMMENDATION_TYPE_SPECIAL =
  'HCS_RECOMMENDATION_TYPE_SPECIAL';

export const HCS_RECOMMENDATION_TYPES = [
  {
    value: 'HCS_RECOMMENDATION_TYPE_STANDARD',
    label: 'Standard',
  },
  {
    value: 'HCS_RECOMMENDATION_TYPE_SPECIAL',
    label: 'Special Wording',
  },
];

const getInitialState = () => ({
  name: '',
  description: '',
  display_order: 0,
  is_for_candidate_profile: false,
  is_publicly_visible: false,
  library: [],
  impacted_targets: [],
  negative_rules: [],
  questionnaire_answer: {},
  is_visible_for: [],
  hcs_title: '',
  hcs_recommendation: '',
  hcs_default_recommendation: 'No Concern',
  hcs_type: HCS_QUESTION_GROUP_TYPE_PHYSICAL,
  hcs_recommendation_type: HCS_RECOMMENDATION_TYPE_STANDARD,
  hcs_special_wording_display: [],
});

const state: QuestionGroupState = {
  questionGroup: getInitialState(),
};

const getters: GetterTree<QuestionGroupState, RootState> = {};

const actions: ActionTree<QuestionGroupState, RootState> = {
  fetchList(context, options) {
    return http.v1.qg.fetchList(options).then(({ data }) => {
      const payload = data;
      const items = payload.data.data;
      return {
        items,
        total: payload.data.total,
      };
    });
  },
  fetch({ commit }, { objectId, config }) {
    return http.v1.qg.fetch(objectId, config).then(({ data }) => {
      commit('SET_QUESTION_GROUP', data.data);
    });
  },
  previewForStandard({ commit }, { objectId, config }) {
    return http.v1.qg.previewForStandard(objectId, config).then((response) => {
      commit('SET_QUESTION_GROUP', response.data.data);
      commit(
        'QuestionnaireAnswer/INIT_REPORT_DATA',
        {
          questionnaire: response.data.data.questionnaire_answer.questionnaire,
          questionnaire_answer: response.data.data.questionnaire_answer,
        },
        { root: true }
      );
    });
  },
  previewForCandidateProfile({ commit }, objectId) {
    return http.v1.qg.previewForCandidateProfile(objectId).then((response) => {
      commit('SET_QUESTION_GROUP', response.data.data);
      commit(
        'CandidateProfile/SET_CANDIDATE_PROFILE',
        response.data.data.candidate_profile,
        { root: true }
      );
    });
  },
  create({ commit }, config) {
    return http.v1.qg.create(config).then(({ data }) => {
      commit('SET_QUESTION_GROUP', data.data);
    });
  },
  update({ commit }, { objectId, config }) {
    return http.v1.qg.update(objectId, config).then(({ data }) => {
      commit('SET_QUESTION_GROUP', data.data);
    });
  },
  transfer({ commit }, { objectId, payload }) {
    return http.v1.qg.transfer(objectId, payload).then(({ data }) => {
      commit('UPDATE_IMPACTED_TARGETS', data.data);
    });
  },
  sortLibrary({ commit }, { objectId, config }) {
    return http.v1.qg.sortLibrary(objectId, config).then((response) => {
      commit('UPDATE_LIBRARY', response.data.data.library);
    });
  },
  sortImpactedTargets({ commit }, { objectId, config }) {
    return http.v1.qg.sortImpactedTargets(objectId, config).then((response) => {
      commit('UPDATE_IMPACTED_TARGETS', response.data.data.impacted_targets);
    });
  },
};

const mutations: MutationTree<QuestionGroupState> = {
  CLEAR_STATE(state) {
    state.questionGroup = getInitialState();
  },
  SET_QUESTION_GROUP(state, payload) {
    state.questionGroup = payload;
  },
  ADD_LIBRARY_ITEM(state, payload) {
    state.questionGroup.library.unshift(payload);
  },
  UPDATE_LIBRARY_ITEM(state, payload) {
    const libraryIndex = state.questionGroup.library.findIndex(
      (item) => item.bridge && item.bridge.object_id === payload.object_id
    );
    state.questionGroup.library[libraryIndex].bridge = Object.assign(
      state.questionGroup.library[libraryIndex].bridge,
      payload
    );

    // Update the item in impacted targets if it existed
    const impactedTargetsIndex = state.questionGroup.impacted_targets.findIndex(
      (item) => item.bridge && item.bridge.object_id === payload.object_id
    );
    if (impactedTargetsIndex > -1) {
      state.questionGroup.impacted_targets[
        impactedTargetsIndex
      ].bridge = Object.assign(
        state.questionGroup.impacted_targets[impactedTargetsIndex].bridge,
        payload
      );
    }
  },
  REMOVE_LIBRARY_ITEM(state, payload) {
    const libraryIndex = state.questionGroup.library.findIndex(
      (item) => item.bridge && item.bridge.object_id === payload
    );
    state.questionGroup.library.splice(libraryIndex, 1);

    // Remove the item from impacted targets if it existed
    const impactedTargetsIndex = state.questionGroup.impacted_targets.findIndex(
      (item) => item.bridge && item.bridge.object_id === payload
    );
    if (impactedTargetsIndex > -1) {
      state.questionGroup.impacted_targets.splice(impactedTargetsIndex, 1);
    }
  },
  UPDATE_LIBRARY(state, payload) {
    state.questionGroup.library = payload;
  },
  UPDATE_IMPACTED_TARGETS(state, payload) {
    state.questionGroup.impacted_targets = payload;
  },
  ADD_HSC_SPECIAL_WORDING_DISPLAY(state) {
    state.questionGroup.hcs_special_wording_display?.push({
      type: 'overall_score', // create this attribute in advance for potential needs of health check report wording
      key: '',
      operator: '>=',
      value: '0',
      recommendation: '',
    });
  },
  REMOVE_HSC_SPECIAL_WORDING_DISPLAY(state, index) {
    state.questionGroup.hcs_special_wording_display?.splice(index, 1);
  },
};

const anzsco: Module<QuestionGroupState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export default anzsco;
